import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { APIHOST } from '../common';
import { getToken } from '../components/auth/AuthContext';

type Order = {
  id: number;
  documentId: string;
  confirmation?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  paymentMethod?: string | null;
  orderStatus?: string | null;
};

export default function HotelAdminOrdersPage() {
  const { hotelId } = useParams<{ hotelId: string }>();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hotelId) return;
    const token = getToken();
    setLoading(true);
    fetch(`${APIHOST}/api/hotel-admin/orders?hotelId=${hotelId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch orders');
        return res.json();
      })
      .then((data) => {
        setOrders(Array.isArray(data?.data) ? data.data : []);
        setError(null);
      })
      .catch((err) => {
        setError(err.message);
        setOrders([]);
      })
      .finally(() => setLoading(false));
  }, [hotelId]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Orders
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {orders.length === 0 ? (
            <Stack sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">No orders found.</Typography>
            </Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Confirmation</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.documentId} hover>
                    <TableCell>{order.confirmation || '—'}</TableCell>
                    <TableCell>
                      {[order.contactFirstName, order.contactLastName].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell>{order.contactEmail || '—'}</TableCell>
                    <TableCell>{order.paymentMethod || '—'}</TableCell>
                    <TableCell>{order.orderStatus || '—'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/hotel-admin/orders/${order.documentId}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
